import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  Renderer2,
  SimpleChanges,
} from '@angular/core';

// This directive targets the goab-dropdown component's internal icon element to set an appropriate aria-label based on the button's label,
// ensuring that screen readers can accurately describe the dropdown's purpose. It uses a retry mechanism to handle cases where the dropdown's
// content may not be immediately available in the DOM.
// Usage: Add the dropdownIconAccessibility directive to any goab-dropdown element and provide a customAriaLabel input to enhance its accessibility
// by ensuring that the internal icon has a meaningful aria-label for screen readers.
@Directive({
  selector: '[dropdownIconAccessibility]',
  standalone: true,
})
export class DropdownIconAccessibilityDirective
  implements AfterViewInit, OnChanges
{
  @Input() customAriaLabel = '';
  private readonly maxAttempts = 10;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    this.updateAriaLabelWithRetry();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customAriaLabel']) {
      this.updateAriaLabelWithRetry();
    }
  }

  private updateAriaLabelWithRetry(attempt = 0): void {
    if (this.updateAriaLabel()) {
      return;
    }

    if (attempt >= this.maxAttempts) {
      return;
    }

    setTimeout(() => {
      this.updateAriaLabelWithRetry(attempt + 1);
    }, 100);
  }

  private updateAriaLabel(): boolean {
    const ariaLabel = this.customAriaLabel?.trim();
    if (!ariaLabel) {
      return false;
    }

    const host = this.el.nativeElement.querySelector(
      'goab-dropdown > goa-dropdown'
    )?.shadowRoot;

    if (!host) {
      return false;
    }

    const inputElement =
      host
        .querySelector('goa-popover')
        ?.shadowRoot?.querySelector('input[role="combobox"]') ??
      host.querySelector('input[role="combobox"]');
    if (inputElement) {
      this.renderer.removeAttribute(inputElement, 'aria-labelledby');
      this.renderer.setAttribute(inputElement, 'aria-label', ariaLabel);
    }

    const iconHost =
      host.querySelector('goa-icon[type="chevron-down"]')?.shadowRoot ??
      host.querySelector('goa-icon')?.shadowRoot;
    const iconElement = iconHost?.querySelector('div[role="img"]');
    if (iconElement) {
      iconElement.setAttribute('aria-label', ariaLabel);
    }

    return !!inputElement || !!iconElement;
  }
}
